/** @jsxImportSource @emotion/react */
import React, { memo, useMemo, useEffect, useCallback } from 'react'
import { MenuServiceSubItemType } from '@signal/core/menuService/types'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import { stripDashesFromAnyGuids } from '@signal/utils/stripDashesFromGuid'
import stripQueryParams from '@signal/utils/stripQueryParams'
import { findBestPrefixMatch } from '@signal/core/menuService/utils'
import {
  TabsContainer,
  TabsScrollContainer,
  TabItem,
  TabLink,
  TabIndicator,
  TabText,
  ScrollButton,
  BlurOverlay
} from './styles'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import { useTabsScroll } from './hooks/useTabsScroll'
import { useDarkModeContext } from '@signal/theme/Context/dark-mode'

export interface TabsNavProps {
  tabs: Array<MenuServiceSubItemType>
  currentPathname?: string
  onNavigate?: (link: string) => void
  basePath?: string
}

export function TabsNav({
  tabs,
  currentPathname,
  onNavigate,
  basePath
}: TabsNavProps): JSX.Element | null {
  const currentSearch = location?.search || ''
  const { isDarkMode } = useDarkModeContext()

  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    isHovering,
    startScrolling,
    stopScrolling,
    scrollToEnd,
    updateScrollAvailability,
    handleContainerHover
  } = useTabsScroll()

  const { activeTabIndex, tabsWithSearch } = useMemo(() => {
    if (!currentPathname || !tabs.length)
      return { activeTabIndex: -1, tabsWithSearch: [] }

    const normalizedPathname = location
      ? stripDashesFromAnyGuids(location.pathname).replace(basePath ?? '', '')
      : ''

    // look for an exact match first
    const exactMatchIndex = tabs.findIndex(
      ({ link }) =>
        normalizedPathname === stripDashesFromAnyGuids(stripQueryParams(link))
    )

    let activeIndex = exactMatchIndex

    if (exactMatchIndex === -1) {
      // take the most specific (longest) link that is a prefix of the current path
      const bestMatch = findBestPrefixMatch(tabs, normalizedPathname)
      activeIndex = bestMatch ? tabs.indexOf(bestMatch) : -1
    }

    // Create tabs with search params preserved
    const tabsWithSearch = tabs.map(tab => ({
      ...tab,
      link: tab.link + currentSearch
    }))

    return { activeTabIndex: activeIndex, tabsWithSearch }
  }, [tabs, currentPathname, currentSearch, basePath])

  useEffect(() => {
    // Check scroll availability when tabs change
    updateScrollAvailability()
  }, [tabsWithSearch, updateScrollAvailability])

  const handleMouseEnterContainer = useCallback(() => {
    handleContainerHover(true)
  }, [handleContainerHover])

  const handleMouseLeaveContainer = useCallback(() => {
    handleContainerHover(false)
  }, [handleContainerHover])

  const handleStartScrolling = useCallback(
    (direction: 'left' | 'right') => () => {
      startScrolling(direction)
    },
    [startScrolling]
  )

  const handleScrollToEnd = useCallback(
    (direction: 'left' | 'right') => () => {
      scrollToEnd(direction)
    },
    [scrollToEnd]
  )

  const handleNavigate = useCallback(
    (link: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      if (onNavigate) {
        onNavigate(link)
      }
    },
    [onNavigate]
  )

  if (!tabs.length) return null

  return (
    <TabsScrollContainer
      onMouseEnter={handleMouseEnterContainer}
      onMouseLeave={handleMouseLeaveContainer}
    >
      {/* Left blur overlay */}
      <BlurOverlay $visible={canScrollLeft} $direction="left" />

      {/* Left scroll button */}
      <ScrollButton
        $visible={canScrollLeft && isHovering}
        $direction="left"
        onMouseEnter={handleStartScrolling('left')}
        onMouseLeave={stopScrolling}
        onClick={handleScrollToEnd('left')}
      >
        <ChevronLeft />
      </ScrollButton>

      <TabsContainer ref={scrollContainerRef}>
        {tabsWithSearch.map((tab, index) => (
          <TabItem
            key={`${tab.link}-${index}`}
            $isActive={index === activeTabIndex}
          >
            <TabLink
              // @ts-ignore
              $isActive={index === activeTabIndex}
              $isDarkMode={isDarkMode}
              onClick={handleNavigate(tab.link)}
            >
              <div>
                <TabText
                  className="tabText"
                  $isActive={index === activeTabIndex}
                  $isDarkMode={isDarkMode}
                >
                  {tab.name}
                </TabText>
                <TabIndicator
                  className="TabIndicator"
                  $isActive={index === activeTabIndex}
                  $isDarkMode={isDarkMode}
                />
              </div>
            </TabLink>
          </TabItem>
        ))}
      </TabsContainer>

      {/* Right blur overlay */}
      <BlurOverlay $visible={canScrollRight} $direction="right" />

      {/* Right scroll button */}
      <ScrollButton
        $visible={canScrollRight && isHovering}
        $direction="right"
        onMouseEnter={handleStartScrolling('right')}
        onMouseLeave={stopScrolling}
        onClick={handleScrollToEnd('right')}
      >
        <ChevronRight />
      </ScrollButton>
    </TabsScrollContainer>
  )
}

export default memo(withCommonSnapshotsOnly(TabsNav))
